import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "services/api";
import { DeviceChartDataResponse } from "types/deviceHooks.types";

const YEAR_MS = 365 * 24 * 60 * 60 * 1000;
export const ALL_TIME_LOOKBACK_MS = 2 * YEAR_MS;

export function useChartChunkQueries(
  serialNumber: string,
  isAllTimeView: boolean,
  chunkFetch: { from: number; to: number },
) {
  const allTimeFetchToRef = useRef(Date.now());

  const chunkQuery = useQuery<DeviceChartDataResponse>({
    queryKey: [
      "deviceChartData",
      serialNumber,
      "chunk",
      chunkFetch.from,
      chunkFetch.to,
    ],
    queryFn: async ({ queryKey }) => {
      const from = queryKey[3] as number;
      const to = queryKey[4] as number;
      const { data } = await api.get<DeviceChartDataResponse>(
        `dpp/device/chart-logs/${serialNumber}?from=${from}&to=${to}`,
      );
      return data;
    },
    enabled: !!serialNumber,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const allTimeQuery = useQuery<DeviceChartDataResponse>({
    queryKey: ["deviceChartData", serialNumber, "ALL_TIME"],
    queryFn: async () => {
      const to = allTimeFetchToRef.current;
      const from = to - ALL_TIME_LOOKBACK_MS;
      const { data } = await api.get<DeviceChartDataResponse>(
        `dpp/device/chart-logs/${serialNumber}?from=${from}&to=${to}`,
      );
      return data;
    },
    enabled: isAllTimeView && !!serialNumber,
    staleTime: 10 * 60 * 1000,
    placeholderData: (prev) => prev,
  });

  return { chunkQuery, allTimeQuery };
}
